import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBaskets } from '../context/BasketContext';
import { apiGet } from '../config/apiClient';

const COLORS = ['#2563eb', '#16a34a', '#eab308', '#dc2626', '#9333ea', '#0d9488', '#ea580c', '#6366f1'];

function BasketPriceChart({ seriesData, cs = '$' }) {
  if (!seriesData || seriesData.length === 0) return null;

  const allPoints = seriesData.flatMap(s => s.data_points.map(p => p.total));
  if (allPoints.length === 0) return null;

  const minVal = Math.min(...allPoints) * 0.95;
  const maxVal = Math.max(...allPoints) * 1.05;
  const range = maxVal - minVal || 1;

  const chartW = 600;
  const chartH = 200;
  const padL = 50;
  const padR = 20;
  const padT = 20;
  const padB = 30;
  const plotW = chartW - padL - padR;
  const plotH = chartH - padT - padB;

  const allDates = [...new Set(seriesData.flatMap(s => s.data_points.map(p => p.date)))].sort();
  const dateCount = allDates.length;

  const xScale = (idx) => padL + (idx / Math.max(dateCount - 1, 1)) * plotW;
  const yScale = (val) => padT + plotH - ((val - minVal) / range) * plotH;

  return (
    <div className="mt-4 p-4 border rounded-lg bg-gray-50 overflow-x-auto">
      <svg viewBox={`0 0 ${chartW} ${chartH}`} width="100%" preserveAspectRatio="xMidYMid meet">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(frac => {
          const y = padT + plotH - frac * plotH;
          const val = minVal + frac * range;
          return (
            <g key={frac}>
              <line x1={padL} y1={y} x2={chartW - padR} y2={y} stroke="#e5e7eb" strokeWidth="0.5" />
              <text x={padL - 5} y={y + 3} fontSize="8" fill="#9ca3af" textAnchor="end">{cs}{val.toFixed(0)}</text>
            </g>
          );
        })}

        {/* Date labels */}
        {allDates.map((date, idx) => {
          if (dateCount > 10 && idx % Math.ceil(dateCount / 6) !== 0 && idx !== dateCount - 1) return null;
          return (
            <text key={date} x={xScale(idx)} y={chartH - 5} fontSize="7" fill="#9ca3af" textAnchor="middle">
              {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </text>
          );
        })}

        {/* Lines */}
        {seriesData.map((series, sIdx) => {
          const color = COLORS[sIdx % COLORS.length];
          const points = series.data_points;
          if (points.length < 2) return null;

          const pathData = points.map((p, i) => {
            const dateIdx = allDates.indexOf(p.date);
            const x = xScale(dateIdx);
            const y = yScale(p.total);
            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
          }).join(' ');

          return (
            <g key={sIdx}>
              <path d={pathData} fill="none" stroke={color} strokeWidth="2" />
              {points.map((p, i) => {
                const dateIdx = allDates.indexOf(p.date);
                return (
                  <circle key={i} cx={xScale(dateIdx)} cy={yScale(p.total)} r="3" fill={color}>
                    <title>{series.vendor_name}: {cs}{p.total.toFixed(2)} on {p.date}</title>
                  </circle>
                );
              })}
            </g>
          );
        })}
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mt-2 justify-center">
        {seriesData.map((series, sIdx) => (
          <div key={sIdx} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: COLORS[sIdx % COLORS.length] }}></span>
            {series.vendor_name}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function BasketDetailPage() {
  const { id } = useParams();
  const { authToken, isAuthenticated } = useAuth();
  const { removeFromBasket, updateItemQuantity, fetchBaskets } = useBaskets();
  const navigate = useNavigate();

  const [basket, setBasket] = useState(null);
  const [storeTotals, setStoreTotals] = useState(null);
  const [priceHistory, setPriceHistory] = useState(null);
  const [inflation, setInflation] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState('$');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError('');

    try {
      const [detailData, histData] = await Promise.all([
        apiGet(`/baskets/${id}`),
        apiGet(`/baskets/${id}/price-history?days=90`).catch(() => null),
      ]);

      setBasket(detailData.data.basket);
      setStoreTotals(detailData.data.store_totals);
      if (detailData.data.currency?.symbol) {
        setCurrencySymbol(detailData.data.currency.symbol);
      }

      if (histData) {
        setPriceHistory(histData.data);
        if (histData.inflation) {
          setInflation(histData.inflation);
        }
        if (histData.currency?.symbol) {
          setCurrencySymbol(histData.currency.symbol);
        }
      }
    } catch (err) {
      console.error('Basket detail error:', err);
      setError(err.data?.message || err.message || 'An error occurred loading the basket.');
    }
    setLoading(false);
  }, [id, authToken]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const handleRemoveItem = async (itemId) => {
    setActionLoading(itemId);
    const success = await removeFromBasket(id, itemId);
    if (success) {
      fetchDetail();
    }
    setActionLoading(null);
  };

  const handleQuantityChange = async (itemId, newQty) => {
    if (newQty < 1 || newQty > 100) return;
    setActionLoading(itemId);
    const success = await updateItemQuantity(id, itemId, newQty);
    if (success) {
      fetchDetail();
    }
    setActionLoading(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Please log in to view baskets.</p>
        <Link to="/login" className="mt-4 inline-block bg-blue-600 text-white px-6 py-2 rounded-lg">Log In</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <div className="animate-spin h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2"></div>
        <p className="text-sm text-gray-500">Loading basket...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Link to="/baskets" className="text-blue-600 hover:underline">Back to Baskets</Link>
      </div>
    );
  }

  if (!basket) return null;

  const isOwner = !basket.is_system;
  const items = basket.items || [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="mb-2">
        <Link to="/baskets" className="text-sm text-blue-600 hover:underline">&larr; All Baskets</Link>
      </div>

      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{basket.name}</h1>
          {basket.description && <p className="text-gray-500 mt-1">{basket.description}</p>}
          {basket.is_system && (
            <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded mt-1 inline-block">
              Featured Basket
            </span>
          )}
        </div>
      </div>

      {/* Store Comparison */}
      {storeTotals && (storeTotals.complete.length > 0 || storeTotals.partial.length > 0) && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Store Price Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="py-2 pr-4">Store</th>
                  <th className="py-2 pr-4">Total Cost</th>
                  <th className="py-2 pr-4">Products Available</th>
                  <th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {storeTotals.complete.map((s, i) => (
                  <tr key={s.vendor_id} className="border-b last:border-0">
                    <td className="py-2.5 pr-4 font-medium text-gray-800">
                      {s.vendor_name}
                      {i === 0 && <span className="ml-2 text-[10px] font-semibold bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Cheapest</span>}
                    </td>
                    <td className="py-2.5 pr-4 font-semibold text-gray-900">{currencySymbol}{s.total.toFixed(2)}</td>
                    <td className="py-2.5 pr-4 text-gray-500">{s.products_available} / {storeTotals.item_count}</td>
                    <td className="py-2.5">
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Complete</span>
                    </td>
                  </tr>
                ))}
                {storeTotals.partial.map((s) => (
                  <tr key={s.vendor_id} className="border-b last:border-0 text-gray-400">
                    <td className="py-2.5 pr-4">{s.vendor_name}</td>
                    <td className="py-2.5 pr-4">{currencySymbol}{s.total.toFixed(2)}*</td>
                    <td className="py-2.5 pr-4">{s.products_available} / {storeTotals.item_count}</td>
                    <td className="py-2.5">
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Partial</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {storeTotals.partial.length > 0 && (
            <p className="text-xs text-gray-400 mt-2">* Partial totals only include available items.</p>
          )}
        </div>
      )}

      {/* Inflation Summary */}
      {inflation && inflation.overall && inflation.overall.percent_change !== null && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            Basket Inflation — Last {inflation.days} Days
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Overall */}
            <div className={`rounded-lg p-4 text-center ${
              inflation.overall.percent_change > 0 ? 'bg-red-50' :
              inflation.overall.percent_change < 0 ? 'bg-green-50' : 'bg-blue-50'
            }`}>
              <div className="text-xs text-gray-500 mb-1">Overall Price Change</div>
              <div className={`text-3xl font-bold ${
                inflation.overall.percent_change > 0 ? 'text-red-600' :
                inflation.overall.percent_change < 0 ? 'text-green-600' : 'text-blue-600'
              }`}>
                {inflation.overall.percent_change > 0 ? '▲' : inflation.overall.percent_change < 0 ? '▼' : '▶'}{' '}
                {Math.abs(inflation.overall.percent_change)}%
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {currencySymbol}{inflation.overall.start_total?.toFixed(2)} → {currencySymbol}{inflation.overall.end_total?.toFixed(2)}
              </div>
              <div className="text-xs text-gray-400">
                {inflation.overall.change > 0 ? 'Increase' : inflation.overall.change < 0 ? 'Decrease' : 'No change'} of {currencySymbol}{Math.abs(inflation.overall.change).toFixed(2)}
              </div>
            </div>

            {/* Highest increase */}
            {inflation.per_vendor.length > 0 && (() => {
              const h = inflation.per_vendor[0];
              return (
                <div className="rounded-lg p-4 text-center bg-red-50/50">
                  <div className="text-xs text-gray-500 mb-1">Highest Increase</div>
                  <div className={`text-2xl font-bold ${h.percent_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {h.percent_change > 0 ? '+' : ''}{h.percent_change}%
                  </div>
                  <div className="font-medium text-sm text-gray-700 mt-1">{h.vendor_name}</div>
                  <div className="text-xs text-gray-400">
                    {currencySymbol}{h.start_total.toFixed(2)} → {currencySymbol}{h.end_total.toFixed(2)}
                  </div>
                </div>
              );
            })()}

            {/* Lowest / Best change */}
            {inflation.per_vendor.length > 1 && (() => {
              const l = inflation.per_vendor[inflation.per_vendor.length - 1];
              return (
                <div className="rounded-lg p-4 text-center bg-green-50/50">
                  <div className="text-xs text-gray-500 mb-1">Lowest / Best Change</div>
                  <div className={`text-2xl font-bold ${l.percent_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {l.percent_change > 0 ? '+' : ''}{l.percent_change}%
                  </div>
                  <div className="font-medium text-sm text-gray-700 mt-1">{l.vendor_name}</div>
                  <div className="text-xs text-gray-400">
                    {currencySymbol}{l.start_total.toFixed(2)} → {currencySymbol}{l.end_total.toFixed(2)}
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Per-vendor table for 3+ vendors */}
          {inflation.per_vendor.length > 2 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Price Change by Store</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="py-1.5 pr-3">Store</th>
                      <th className="py-1.5 pr-3">Start</th>
                      <th className="py-1.5 pr-3">Current</th>
                      <th className="py-1.5 pr-3">Change</th>
                      <th className="py-1.5">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inflation.per_vendor.map((v, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 text-gray-700">{v.vendor_name}</td>
                        <td className="py-1.5 pr-3">{currencySymbol}{v.start_total.toFixed(2)}</td>
                        <td className="py-1.5 pr-3">{currencySymbol}{v.end_total.toFixed(2)}</td>
                        <td className={`py-1.5 pr-3 ${v.change > 0 ? 'text-red-600' : v.change < 0 ? 'text-green-600' : ''}`}>
                          {v.change > 0 ? '+' : ''}{currencySymbol}{v.change.toFixed(2)}
                        </td>
                        <td className="py-1.5">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            v.percent_change > 0 ? 'bg-red-100 text-red-700' :
                            v.percent_change < 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {v.percent_change > 0 ? '+' : ''}{v.percent_change}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Price History Chart */}
      {priceHistory && priceHistory.length > 0 && (
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-1">Basket Cost Over Time</h2>
          <p className="text-xs text-gray-400 mb-2">Total basket cost per store over the last 90 days</p>
          <BasketPriceChart seriesData={priceHistory} cs={currencySymbol} />
        </div>
      )}

      {/* Items List */}
      <div className="bg-white rounded-xl shadow-md p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          Items ({items.length})
        </h2>

        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <p>This basket has no items yet.</p>
            {isOwner && (
              <p className="text-sm mt-1">Browse products and use the "Add to Basket" button to add items.</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map(item => {
              const product = item.product;
              const lowestPrice = product?.product_prices?.filter(p => p.status === 1)
                ?.reduce((min, p) => Math.min(min, parseFloat(p.price)), Infinity);

              return (
                <div key={item.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {product?.image_url && (
                      <img src={product.image_url} alt="" className="w-10 h-10 object-contain rounded bg-gray-100 shrink-0" />
                    )}
                    <div className="min-w-0">
                      <Link to={`/product/${product?.id}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm truncate block">
                        {product?.name || `Product #${item.product_id}`}
                      </Link>
                      {lowestPrice !== Infinity && (
                        <span className="text-xs text-gray-400">From {currencySymbol}{lowestPrice.toFixed(2)}</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    {isOwner ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1 || actionLoading === item.id}
                          className="w-7 h-7 flex items-center justify-center border rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 text-sm"
                        >-</button>
                        <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                        <button
                          onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                          disabled={actionLoading === item.id}
                          className="w-7 h-7 flex items-center justify-center border rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 text-sm"
                        >+</button>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">x{item.quantity}</span>
                    )}

                    {isOwner && (
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={actionLoading === item.id}
                        className="text-red-400 hover:text-red-600 transition p-1"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
